import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FileCode, ChevronRight, Layers } from 'lucide-react';
import theme from '../theme';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STANDARD_COLORS = {
  ERC721: theme.colors.neon.blue,
  ERC1155: theme.colors.accent.teal,
  ERC20: theme.colors.accent.salmon,
};

const ContractTemplatesPage = ({ user }) => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [tplRes, draftRes] = await Promise.all([
        axios.get(`${API}/contracts/templates`, { headers }),
        axios.get(`${API}/contracts/drafts`, { headers })
      ]);
      setTemplates(tplRes.data.templates || []);
      setDrafts(draftRes.data.drafts || []);
    } catch (err) {
      console.error('Error fetching templates:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: theme.colors.text.secondary }}>Loading templates...</div>;
  }

  return (
    <div style={{ padding: '40px' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 data-testid="templates-page-title" style={{
          fontFamily: theme.fonts.heading,
          fontSize: '28px',
          fontWeight: 700,
          color: theme.colors.text.primary,
          marginBottom: '6px'
        }}>
          Smart Contract Templates
        </h1>
        <p style={{ fontSize: '14px', color: theme.colors.text.secondary }}>
          Choose a template to generate a contract draft for your portfolio
        </p>
      </div>

      {/* Templates Grid */}
      <div style={{ marginBottom: '40px' }}>
        <h2 style={{ fontSize: '16px', fontWeight: 600, color: theme.colors.text.primary, marginBottom: '16px' }}>
          Available Templates
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '16px' }}>
          {templates.map((tpl) => {
            const color = STANDARD_COLORS[tpl.standard] || theme.colors.primary;
            const fieldCount = Object.keys(tpl.fields_schema || {}).length;
            return (
              <div
                key={tpl.id}
                data-testid={`template-card-${tpl.id}`}
                onClick={() => navigate(`/creator/contracts/drafts/new?template_id=${tpl.id}`)}
                style={{
                  background: theme.colors.bg.card,
                  borderRadius: theme.radius.card,
                  border: `1px solid ${theme.colors.borders}`,
                  padding: '24px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: theme.colors.shadows.sm,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '16px'
                }}
                onMouseOver={(e) => { e.currentTarget.style.boxShadow = theme.colors.shadows.md; e.currentTarget.style.borderColor = color; }}
                onMouseOut={(e) => { e.currentTarget.style.boxShadow = theme.colors.shadows.sm; e.currentTarget.style.borderColor = theme.colors.borders; }}
              >
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '14px',
                  background: `${color}10`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <FileCode size={24} style={{ color }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: 600, color: theme.colors.text.primary }}>
                      {tpl.name}
                    </h3>
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: '10px',
                      fontSize: '11px',
                      fontWeight: 600,
                      background: `${color}12`,
                      color: color
                    }}>
                      {tpl.standard}
                    </span>
                    {tpl.standard === 'ERC20' && (
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: '10px',
                        fontSize: '10px',
                        fontWeight: 700,
                        background: `${theme.colors.accent.salmon}12`,
                        color: theme.colors.accent.salmon,
                        letterSpacing: '0.3px',
                        textTransform: 'uppercase'
                      }}>
                        Advanced
                      </span>
                    )}
                  </div>
                  <p style={{ fontSize: '13px', color: theme.colors.text.muted, marginBottom: '10px' }}>
                    v{tpl.version} &middot; {fieldCount} configurable fields
                  </p>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '13px',
                    fontWeight: 600,
                    color: color
                  }}>
                    Create Draft <ChevronRight size={14} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Existing Drafts */}
      <div>
        <h2 style={{ fontSize: '16px', fontWeight: 600, color: theme.colors.text.primary, marginBottom: '16px' }}>
          Your Drafts ({drafts.length})
        </h2>
        {drafts.length === 0 ? (
          <div style={{
            background: theme.colors.bg.secondary,
            borderRadius: theme.radius.card,
            padding: '40px',
            textAlign: 'center',
            border: `1px solid ${theme.colors.borders}`
          }}>
            <Layers size={40} style={{ color: theme.colors.text.muted, marginBottom: '12px' }} />
            <p style={{ color: theme.colors.text.secondary, fontSize: '14px' }}>
              No drafts yet. Select a template above to create one.
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {drafts.map((d) => {
              const color = STANDARD_COLORS[d.standard] || theme.colors.primary;
              return (
                <div
                  key={d.id}
                  data-testid={`draft-row-${d.id}`}
                  onClick={() => navigate(`/creator/contracts/drafts/${d.id}`)}
                  style={{
                    background: theme.colors.bg.card,
                    borderRadius: theme.radius.sm,
                    border: `1px solid ${theme.colors.borders}`,
                    padding: '16px 20px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    transition: 'all 0.15s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.background = theme.colors.bg.secondary}
                  onMouseOut={(e) => e.currentTarget.style.background = theme.colors.bg.card}
                >
                  <FileCode size={18} style={{ color, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '14px', fontWeight: 600, color: theme.colors.text.primary }}>
                        {d.params?.contract_name || d.template_name || 'Untitled'}
                      </span>
                      <span style={{
                        padding: '2px 7px',
                        borderRadius: '8px',
                        fontSize: '10px',
                        fontWeight: 600,
                        background: d.status === 'READY' ? `${theme.colors.status.success}15` : `${theme.colors.status.warning}15`,
                        color: d.status === 'READY' ? theme.colors.status.success : theme.colors.status.warning
                      }}>
                        {d.status}
                      </span>
                    </div>
                    <span style={{ fontSize: '12px', color: theme.colors.text.muted }}>
                      {d.standard} &middot; {d.template_name} &middot; {new Date(d.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <ChevronRight size={16} style={{ color: theme.colors.text.muted }} />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContractTemplatesPage;
